/**
 * The web client's picture of a game. Twin of `app/lib/game.dart`.
 *
 * Authoritative about nothing except the fog — every position arrives from
 * the server, and the only thing this class decides on its own is what the
 * player is allowed to see. The rotation is applied in the renderer, not
 * here.
 *
 * There is no client-side bot. The Dart app plays a bot after thirty
 * seconds of waiting alone; the web deliberately does not — see
 * docs/DEVELOPER_GUIDE.md for why.
 */

import type { Role } from '../../shared/protocol.js';
import type { LeaderboardEntry } from '../../shared/protocol.js';
import {
  canMove,
  centerIndex,
  generate,
  idx as indexOf,
  step,
  type Maze,
} from '../../shared/maze.js';
import type { ClientEvent, GameClient } from './net.js';

/** How far you can see, in cells, as a Chebyshev radius around anywhere stood. */
export const visionRadius = 2;

/**
 * Phases mirror the app's — down to the name — so a screenshot from one
 * client can be described in the vocabulary of the other.
 */
export type Phase =
  | 'idle'
  | 'connecting'
  | 'queued'
  | 'playing'
  | 'finished'
  | 'lost'
  | 'leaderboardLoading'
  | 'leaderboard';

export type GameResult = 'win' | 'loss';
export type OverReason = 'reached' | 'resigned' | 'disconnected';

/**
 * A minimal `EventTarget` — a listener that fires on every state change.
 * There is no framework, so callers subscribe once and re-render from the
 * fields they care about.
 */
type ChangeListener = () => void;

export interface GameSnapshot {
  phase: Phase;
  maze: Maze | null;
  role: Role | null;
  you: number;
  opponent: number;
  youMoves: number;
  opponentMoves: number;
  opponentName: string;
  playerName: string;
  friendName: string;
  waitingFor: string | null;
  revealed: ReadonlySet<number>;
  result: GameResult | null;
  reason: OverReason | null;
  notice: string | null;
  leaderboard: readonly LeaderboardEntry[];
}

export class GameModel {
  readonly client: GameClient;

  phase: Phase = 'idle';
  maze: Maze | null = null;
  role: Role | null = null;
  you = 0;
  opponent = 0;
  youMoves = 0;
  opponentMoves = 0;
  opponentName = '';
  playerName = '';
  friendName = '';
  waitingFor: string | null = null;
  revealed: Set<number> = new Set();
  result: GameResult | null = null;
  reason: OverReason | null = null;
  notice: string | null = null;
  leaderboard: readonly LeaderboardEntry[] = [];

  private readonly listeners = new Set<ChangeListener>();
  private readonly unsubscribe: () => void;

  constructor(client: GameClient) {
    this.client = client;
    this.unsubscribe = client.on((event) => this.apply(event));
  }

  onChange(listener: ChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  snapshot(): GameSnapshot {
    return {
      phase: this.phase,
      maze: this.maze,
      role: this.role,
      you: this.you,
      opponent: this.opponent,
      youMoves: this.youMoves,
      opponentMoves: this.opponentMoves,
      opponentName: this.opponentName,
      playerName: this.playerName,
      friendName: this.friendName,
      waitingFor: this.waitingFor,
      revealed: this.revealed,
      result: this.result,
      reason: this.reason,
      notice: this.notice,
      leaderboard: this.leaderboard,
    };
  }

  /** Queue for a random game, or for one particular friend by name. */
  async play(name: string, withFriend?: string): Promise<void> {
    this.playerName = name.trim();
    const friend = (withFriend ?? '').trim();
    this.waitingFor = friend.length === 0 ? null : friend;
    if (friend.length > 0) this.friendName = friend;
    this.phase = 'connecting';
    this.notice = null;
    this.notify();
    try {
      await this.client.connect();
      this.client.queue(this.playerName, this.waitingFor ?? undefined);
    } catch {
      this.phase = 'lost';
      this.notice = 'Could not reach the server.';
      this.notify();
    }
  }

  async openLeaderboard(): Promise<void> {
    this.phase = 'leaderboardLoading';
    this.leaderboard = [];
    this.notice = null;
    this.notify();
    try {
      await this.client.connect();
      this.client.requestLeaderboard();
    } catch {
      this.phase = 'leaderboard';
      this.notice = 'Could not load the leaderboard.';
      this.notify();
    }
  }

  closeLeaderboard(): void {
    this.backToMenu();
  }

  /**
   * Concede or cancel. The server treats a resign in the lobby as "leave
   * the queue", so this is also the Cancel button while waiting.
   */
  resign(): void {
    this.client.resign();
  }

  /** Ask to step one cell in the direction the player pushed *on screen*. */
  tryMove(screenDir: number): void {
    const maze = this.maze;
    if (this.phase !== 'playing' || maze === null) return;
    const dir = this.toMazeDir(screenDir);
    if (!canMove(maze, this.you, dir)) return;
    this.client.move(dir);
  }

  /** Where a maze cell is drawn — screen-space, not board-space. */
  toViewCell(cell: number): number {
    const maze = this.maze;
    if (maze === null || this.role !== 'bottom') return cell;
    return maze.size * maze.size - 1 - cell;
  }

  /**
   * The maze direction a push on the screen means. Half a turn reverses
   * every direction — press down for the bottom seat and the pawn should
   * still move down the screen, which is a northward step in the maze.
   */
  toMazeDir(screenDir: number): number {
    return this.role === 'bottom' ? (screenDir + 2) % 4 : screenDir;
  }

  /** True when the player has earned the right to see this cell's walls. */
  isVisible(cell: number): boolean {
    if (this.maze !== null && cell === centerIndex(this.maze.size)) return true;
    return this.revealed.has(cell);
  }

  /** Back to the menu, socket left open. */
  backToMenu(): void {
    this.phase = 'idle';
    this.waitingFor = null;
    this.maze = null;
    this.role = null;
    this.result = null;
    this.reason = null;
    this.leaderboard = [];
    this.revealed = new Set();
    this.notice = null;
    this.notify();
  }

  private apply(event: ClientEvent): void {
    switch (event.type) {
      case 'queued':
        this.phase = 'queued';
        this.notice = null;
        break;
      case 'start': {
        this.waitingFor = null;
        this.maze = generate(event.seed, event.size);
        this.role = event.role;
        this.you = event.you;
        this.opponent = event.opponent;
        this.opponentName = event.opponentName;
        this.youMoves = 0;
        this.opponentMoves = 0;
        this.result = null;
        this.reason = null;
        this.revealed = new Set();
        this.reveal(event.you);
        this.phase = 'playing';
        this.notice = null;
        break;
      }
      case 'state':
        this.you = event.you;
        this.opponent = event.opponent;
        this.youMoves = event.youMoves;
        this.opponentMoves = event.opponentMoves;
        this.reveal(event.you);
        break;
      case 'over':
        this.result = event.result;
        this.reason = event.reason;
        this.youMoves = event.youMoves;
        this.opponentMoves = event.opponentMoves;
        this.phase = 'finished';
        break;
      case 'error':
        // A wall bump is routine; the local check catches it first, and the
        // banner would just get in the way. Anything else is worth showing.
        if (event.code !== 'wall') {
          this.notice = event.message;
          if (this.phase === 'leaderboardLoading') this.phase = 'leaderboard';
        }
        break;
      case 'pong':
        return;
      case 'leaderboard':
        // A stale response must not replace a game that started while the
        // request was in flight.
        if (this.phase !== 'leaderboardLoading' && this.phase !== 'leaderboard') {
          return;
        }
        this.leaderboard = event.players;
        this.phase = 'leaderboard';
        this.notice = null;
        break;
      case 'disconnected':
        if (
          this.phase === 'playing' ||
          this.phase === 'queued' ||
          this.phase === 'connecting' ||
          this.phase === 'leaderboardLoading'
        ) {
          this.phase = 'lost';
          this.notice = 'Connection lost.';
        }
        break;
    }
    this.notify();
  }

  /**
   * Lift the fog around a cell. A square rather than a circle because the
   * walls are drawn on a square grid and a round edge to the revealed area
   * reads as a rendering bug rather than as a torch.
   */
  private reveal(at: number): void {
    const maze = this.maze;
    if (maze === null) return;
    const size = maze.size;
    const r = Math.floor(at / size);
    const c = at % size;
    for (let dy = -visionRadius; dy <= visionRadius; dy++) {
      for (let dx = -visionRadius; dx <= visionRadius; dx++) {
        const rr = r + dy;
        const cc = c + dx;
        if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
        this.revealed.add(indexOf(size, rr, cc));
      }
    }
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener();
  }

  dispose(): void {
    this.unsubscribe();
    this.listeners.clear();
  }

  /** For tests: push an event as if it had come off the socket. */
  applyForTest(event: ClientEvent): void {
    this.apply(event);
  }

  /** Where the goal is on the board this player is playing. */
  centerCell(): number {
    return this.maze === null ? -1 : centerIndex(this.maze.size);
  }

  /**
   * The cell you would arrive at from `this.you` in maze direction `dir`,
   * or -1 if a wall or edge stops you. Used by the renderer to preview
   * the next step.
   */
  wouldStepTo(dir: number): number {
    if (this.maze === null) return -1;
    if (!canMove(this.maze, this.you, dir)) return -1;
    return step(this.maze, this.you, dir);
  }
}
